import React from "react";

const STATUS_TAG = {
  draft: "tag attn",
  posted: "tag ok",
  voided: "tag idle",
  cancelled: "tag idle",
  approved: "tag attn",
  ready_to_post: "tag attn",
  pending_review: "tag attn",
  under_review: "tag attn",
  confirmed_duplicate: "tag ok",
  kept_both: "tag idle",
  valid_repeat_activity: "tag idle",
  false_positive: "tag idle",
};

export function StatusTag({ status, testid }) {
  const cls = STATUS_TAG[status] || "tag idle";
  return (
    <span className={cls} data-testid={testid}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

/**
 * Compact toolbar of filter controls. Children are rendered inline.
 */
export function RegisterToolbar({ children, testid = "register-toolbar" }) {
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "12px 16px", marginBottom: 16,
        background: "var(--surface-elev)",
        border: "1px solid var(--line)", borderRadius: 6,
      }}
    >
      {children}
    </div>
  );
}

export function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
      <span className="muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={testid}
        style={{
          padding: "4px 8px", fontSize: 13, fontFamily: "inherit",
          background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 4,
        }}
      >
        <option value="">All</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export function FilterInput({ label, value, onChange, placeholder, testid }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
      <span className="muted" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={placeholder}
        data-testid={testid}
        style={{
          padding: "4px 8px", fontSize: 13, fontFamily: "inherit",
          background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 4,
          minWidth: 180,
        }}
      />
    </label>
  );
}

/**
 * Table renderer. Columns: array of { key, label, width, align, render(row) }.
 */
export function RegisterTable({ columns, rows, onRowClick, emptyText = "No rows.", testid = "register-table" }) {
  if (!rows || rows.length === 0) {
    return (
      <div data-testid={testid} style={{
        border: "1px solid var(--line)", borderRadius: 6,
        background: "var(--surface)",
      }}>
        <div className="empty" data-testid={`${testid}-empty`} style={{ border: "none" }}>
          {emptyText}
        </div>
      </div>
    );
  }
  return (
    <div data-testid={testid} style={{
      border: "1px solid var(--line)", borderRadius: 6, overflow: "hidden",
      background: "var(--surface)",
    }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: columns.map(c => c.width || "1fr").join(" "),
        columnGap: 16,
        background: "var(--surface-elev)",
        padding: "10px 14px",
        borderBottom: "1px solid var(--line)",
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
        fontWeight: 600, color: "var(--ink-mute)",
      }}>
        {columns.map(c => (
          <div key={c.key} style={{ textAlign: c.align || "left" }}>{c.label}</div>
        ))}
      </div>
      {rows.map((row, idx) => (
        <div
          key={row.id || idx}
          data-testid={`${testid}-row-${row.id || idx}`}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          style={{
            display: "grid",
            gridTemplateColumns: columns.map(c => c.width || "1fr").join(" "),
            columnGap: 16,
            padding: "12px 14px",
            borderBottom: idx === rows.length - 1 ? "none" : "1px solid var(--line)",
            cursor: onRowClick ? "pointer" : "default",
            fontSize: 13,
            alignItems: "center",
          }}
          className={onRowClick ? "register-row-hover" : ""}
        >
          {columns.map(c => (
            <div key={c.key} style={{ textAlign: c.align || "left", overflow: "hidden", textOverflow: "ellipsis" }}>
              {c.render ? c.render(row) : row[c.key]}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
